import type { CoreState } from "../game/runtime/types";

export function drawCorePattern(context: CanvasRenderingContext2D, core: CoreState, patternId: string, color: string): void {
  if (patternId === "clean_core") {
    return;
  }

  context.save();
  clipCore(context, core);
  if (patternId === "checker_core") {
    drawCheckerPattern(context, core, color);
  } else if (patternId === "dice_core") {
    drawDicePattern(context, core, color);
  } else if (patternId === "prism_core") {
    drawPrismPattern(context, core, color);
  } else if (patternId === "hollow_core") {
    drawHollowPattern(context, core);
  } else if (patternId === "target_core") {
    drawTargetPattern(context, core, color);
  } else if (patternId === "split_core") {
    drawSplitPattern(context, core, color);
  } else if (patternId === "spark_core") {
    drawSparkPattern(context, core, color);
  } else if (patternId === "twin_core") {
    drawTwinPattern(context, core, color);
  } else if (patternId === "crown_core") {
    drawCrownPattern(context, core, color);
  }
  context.restore();
}

function clipCore(context: CanvasRenderingContext2D, core: CoreState): void {
  context.beginPath();
  context.arc(core.position.x, core.position.y, core.radius * 1.12, 0, Math.PI * 2);
  context.clip();
}

function drawCheckerPattern(context: CanvasRenderingContext2D, core: CoreState, color: string): void {
  const cell = core.radius * 0.55;
  const startX = core.position.x - cell * 2;
  const startY = core.position.y - cell * 2;
  for (let row = 0; row < 4; row += 1) {
    for (let column = 0; column < 4; column += 1) {
      context.fillStyle = (row + column) % 2 === 0 ? "rgba(233, 251, 255, 0.72)" : "rgba(3, 8, 12, 0.46)";
      context.fillRect(startX + column * cell, startY + row * cell, cell, cell);
    }
  }
  context.strokeStyle = colorWithAlpha(color, 0.7);
  context.lineWidth = 1.5;
  context.strokeRect(startX, startY, cell * 4, cell * 4);
}

function drawDicePattern(context: CanvasRenderingContext2D, core: CoreState, color: string): void {
  const offset = core.radius * 0.42;
  const pipRadius = core.radius * 0.16;
  const pips = [
    [-offset, -offset],
    [offset, -offset],
    [0, 0],
    [-offset, offset],
    [offset, offset]
  ] as const;
  context.fillStyle = colorWithAlpha("#ffffff", 0.72);
  context.shadowBlur = 6;
  context.shadowColor = colorWithAlpha(color, 0.72);
  for (const [x, y] of pips) {
    context.beginPath();
    context.arc(core.position.x + x, core.position.y + y, pipRadius, 0, Math.PI * 2);
    context.fill();
  }
}

function drawPrismPattern(context: CanvasRenderingContext2D, core: CoreState, color: string): void {
  const radius = core.radius * 0.9;
  context.strokeStyle = colorWithAlpha("#ffffff", 0.74);
  context.lineWidth = 2;
  context.shadowBlur = 9;
  context.shadowColor = colorWithAlpha(color, 0.76);
  context.beginPath();
  for (let index = 0; index < 3; index += 1) {
    const angle = -Math.PI / 2 + index * ((Math.PI * 2) / 3);
    const x = core.position.x + Math.cos(angle) * radius;
    const y = core.position.y + Math.sin(angle) * radius;
    if (index === 0) {
      context.moveTo(x, y);
    } else {
      context.lineTo(x, y);
    }
  }
  context.closePath();
  context.stroke();
  context.beginPath();
  context.moveTo(core.position.x, core.position.y);
  context.lineTo(core.position.x, core.position.y - radius);
  context.moveTo(core.position.x, core.position.y);
  context.lineTo(core.position.x + Math.cos(Math.PI / 6) * radius, core.position.y + Math.sin(Math.PI / 6) * radius);
  context.stroke();
}

function drawHollowPattern(context: CanvasRenderingContext2D, core: CoreState): void {
  const gradient = context.createRadialGradient(core.position.x, core.position.y, 1, core.position.x, core.position.y, core.radius * 0.72);
  gradient.addColorStop(0, "rgba(3, 8, 12, 0.82)");
  gradient.addColorStop(1, "rgba(3, 8, 12, 0.16)");
  context.fillStyle = gradient;
  context.beginPath();
  context.arc(core.position.x, core.position.y, core.radius * 0.66, 0, Math.PI * 2);
  context.fill();
}

function drawTargetPattern(context: CanvasRenderingContext2D, core: CoreState, color: string): void {
  context.strokeStyle = colorWithAlpha("#ffffff", 0.64);
  context.lineWidth = 2;
  context.shadowBlur = 7;
  context.shadowColor = colorWithAlpha(color, 0.7);
  for (const radius of [core.radius * 0.32, core.radius * 0.68]) {
    context.beginPath();
    context.arc(core.position.x, core.position.y, radius, 0, Math.PI * 2);
    context.stroke();
  }
}

function drawSplitPattern(context: CanvasRenderingContext2D, core: CoreState, color: string): void {
  context.strokeStyle = colorWithAlpha("#ffffff", 0.72);
  context.lineWidth = 3;
  context.shadowBlur = 8;
  context.shadowColor = colorWithAlpha(color, 0.72);
  context.beginPath();
  context.moveTo(core.position.x - core.radius, core.position.y - core.radius * 0.18);
  context.lineTo(core.position.x + core.radius, core.position.y + core.radius * 0.18);
  context.stroke();
}

function drawSparkPattern(context: CanvasRenderingContext2D, core: CoreState, color: string): void {
  context.strokeStyle = colorWithAlpha("#ffffff", 0.74);
  context.lineWidth = 2;
  context.shadowBlur = 9;
  context.shadowColor = colorWithAlpha(color, 0.78);
  for (let index = 0; index < 6; index += 1) {
    const angle = index * (Math.PI / 3);
    context.beginPath();
    context.moveTo(core.position.x + Math.cos(angle) * core.radius * 0.18, core.position.y + Math.sin(angle) * core.radius * 0.18);
    context.lineTo(core.position.x + Math.cos(angle) * core.radius * 0.82, core.position.y + Math.sin(angle) * core.radius * 0.82);
    context.stroke();
  }
}

function drawTwinPattern(context: CanvasRenderingContext2D, core: CoreState, color: string): void {
  context.fillStyle = colorWithAlpha("#ffffff", 0.68);
  context.shadowBlur = 8;
  context.shadowColor = colorWithAlpha(color, 0.76);
  for (const offset of [-0.36, 0.36]) {
    context.beginPath();
    context.arc(core.position.x + core.radius * offset, core.position.y, core.radius * 0.24, 0, Math.PI * 2);
    context.fill();
  }
}

function drawCrownPattern(context: CanvasRenderingContext2D, core: CoreState, color: string): void {
  const width = core.radius * 1.18;
  const baseY = core.position.y + core.radius * 0.35;
  context.strokeStyle = colorWithAlpha("#ffffff", 0.76);
  context.lineWidth = 2.5;
  context.lineJoin = "round";
  context.shadowBlur = 9;
  context.shadowColor = colorWithAlpha(color, 0.78);
  context.beginPath();
  context.moveTo(core.position.x - width / 2, baseY);
  context.lineTo(core.position.x - width * 0.28, core.position.y - core.radius * 0.22);
  context.lineTo(core.position.x, core.position.y - core.radius * 0.46);
  context.lineTo(core.position.x + width * 0.28, core.position.y - core.radius * 0.22);
  context.lineTo(core.position.x + width / 2, baseY);
  context.stroke();
}

function colorWithAlpha(hexColor: string, alpha: number): string {
  const red = Number.parseInt(hexColor.slice(1, 3), 16);
  const green = Number.parseInt(hexColor.slice(3, 5), 16);
  const blue = Number.parseInt(hexColor.slice(5, 7), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
